import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  LockReset,
  CheckCircleOutline,
  MarkEmailRead,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

// Uses raw axios (not the shared instance) so the 401 interceptor can't hijack
// an expired-link error and bounce the user to /login.
const API = process.env.REACT_APP_API_URL || "";
const PASSWORD_HINT = "At least 8 characters with a letter and a number.";

const emailFromToken = (token) => {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return "";
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json);
    return String(payload?.email || "").trim().toLowerCase();
  } catch {
    return "";
  }
};

function SetPassword() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [account, setAccount] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const hintEmail = useMemo(() => emailFromToken(token), [token]);

  useEffect(() => {
    if (!token) {
      setError("Missing activation token. Please use the link from your email.");
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/users/set-password-context`, { params: { token } })
      .then((res) => setAccount(res.data.result))
      .catch((err) => {
        setError(
          err.response?.data?.message || "This link is invalid or has expired."
        );
        setErrorCode(err.response?.data?.code || "TOKEN_INVALID");
        setResendEmail(emailFromToken(token));
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleResend = async (e) => {
    e?.preventDefault?.();
    const email = String(resendEmail || hintEmail || "").trim().toLowerCase();
    if (!email.includes("@")) {
      toast.warning("Enter the email address used for your account.");
      return;
    }
    setResending(true);
    try {
      const res = await axios.post(`${API}/users/resend-activation`, {
        email,
        token: token || undefined,
      });
      setResent(true);
      toast.success(res.data?.message || "If that account exists, a new link was sent.");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not resend activation link."
      );
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.warning("Password must be at least 8 characters.");
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      return toast.warning("Password must include at least one letter and one number.");
    }
    if (password !== confirm) return toast.warning("Passwords do not match.");

    setSubmitting(true);
    try {
      await axios.post(`${API}/users/set-password`, { token, password });
      setDone(true);
      toast.success("Password set! You can now sign in.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      const msg = err.response?.data?.message || "Could not set password. Try again.";
      const code = err.response?.data?.code;
      if (err.response?.status === 401) {
        setError(msg);
        setErrorCode(code || "TOKEN_EXPIRED");
        setResendEmail(emailFromToken(token));
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Need a new activation link?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {errorCode === "TOKEN_EXPIRED"
              ? "Your link expired. Enter your work email and we’ll send a fresh activation link."
              : "Enter your work email and we’ll send a fresh activation link if an account exists."}
          </Typography>

          {resent ? (
            <Alert severity="success" icon={<MarkEmailRead />} sx={{ mb: 2 }}>
              Check your inbox (and spam) for a new activation email, then open the latest link.
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleResend}>
              <TextField
                fullWidth
                type="email"
                label="Work email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                sx={{ mb: 2 }}
                required
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={resending}
              >
                {resending ? "Sending…" : "Resend activation link"}
              </Button>
            </Box>
          )}

          <Button
            fullWidth
            sx={{ mt: 2 }}
            onClick={() => navigate("/login")}
          >
            Back to sign in
          </Button>
        </Paper>
        <ToastContainer position="top-center" autoClose={2800} />
      </Container>
    );
  }

  if (done) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 5, textAlign: "center" }}>
          <CheckCircleOutline color="success" sx={{ fontSize: 64, mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>
            Account activated
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting you to sign in…
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", py: 4 }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ overflow: "hidden" }}>
          <Box sx={{ p: 3, color: "#fff", background: "linear-gradient(135deg,#4f46e5,#7c3aed)" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LockReset />
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Set your password
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Activate your account to sign in and manage your assigned devices.
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 2.5, md: 4 } }}>
            {account && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Setting password for <strong>{account.name || account.email}</strong> (
                {account.email})
              </Alert>
            )}
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="New password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 2 }}
                required
                helperText={PASSWORD_HINT}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((p) => !p)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirm password"
                type={showPassword ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                sx={{ mb: 3 }}
                required
                error={Boolean(confirm) && confirm !== password}
                helperText={
                  confirm && confirm !== password
                    ? "Passwords do not match"
                    : "Re-enter the same password"
                }
              />
              <Button type="submit" variant="contained" size="large" fullWidth disabled={submitting}>
                {submitting ? "Saving…" : "Set password & continue"}
              </Button>
            </form>
          </Box>
        </Paper>
      </Container>
      <ToastContainer position="top-center" autoClose={2500} />
    </Box>
  );
}

export default SetPassword;
